/**
 * Site-wide SEO / AEO constants.
 *
 * These are the defaults every page inherits. Per-route overrides live in
 * `routeMeta.ts`; the values here back the home page and act as the fallback
 * for anything not explicitly listed.
 *
 * NOTE: `SITE_URL` is the canonical production origin used for <link rel="canonical">,
 * og:url and sitemap entries. If the production domain is not cellocloset.com,
 * change it here (single source of truth) and regenerate the sitemap.
 */
export const SITE_URL = "https://cellocloset.com";

export const SITE_NAME = "Cello";

/** Home / fallback title. Kept identical to the long-standing brand title. */
export const DEFAULT_TITLE = "Cello: Your hyper-personalized stylist";

export const DEFAULT_DESCRIPTION =
  "Cello pairs you with a real expert stylist, powered by AI, to create hyper-personalized outfit recommendations from your own wardrobe.";

/** Self-hosted social image (already shipped in /public). */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/** Brand logo for structured data (Organization/publisher logo — distinct from the wide OG banner). */
export const DEFAULT_LOGO = `${SITE_URL}/logo.png`;

/** Absolute-ise a path against the canonical origin (for canonical/og:url). */
export const absoluteUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

/**
 * Verified brand social / third-party profile URLs, fed into Organization.sameAs
 * so search + AI answer engines can resolve "Cello" to one unambiguous entity.
 * Source of truth — also mirrored in the static Organization JSON-LD in index.html.
 */
export const SOCIAL_URLS = {
  instagram: "https://instagram.com/hellocellocloset",
  linkedin: "https://www.linkedin.com/company/107869213",
} as const;

/** Public contact email surfaced in Organization.contactPoint. */
export const CONTACT_EMAIL = "hello@cellocloset.com";

/** Cello's founders — used for Organization.founder and Person schema. */
export const FOUNDERS = ["Jinny Chen", "Caden Broussard"] as const;

/** Stable @id anchors so the static (index.html) and client-rendered Organization/
 *  WebSite nodes are merged as one entity by search engines instead of competing. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
